/**
 * The capabilities Journal home borrows from surfaces it may not import.
 *
 * `03-ENGINEERING-SEAMS.md` §1.4 forbids one feature folder importing another,
 * and §4 gives the composer to P35, the reader to P38, the goals workspace to
 * P39 and the recovery export to P38. Journal home *hosts* all four — F-JR-02's
 * Write button, F-JR-05's Continue writing, F-JR-08's Goals workspace card,
 * F-JR-13's row tap, F-JR-04's export — but it owns none of them.
 *
 * So they arrive as capabilities a host supplies, in one context, defaulting to
 * "not composed here". That default is truthful rather than a stub: §8.6 says
 * an unavailable control is an inert label reading "{title}, unavailable" and
 * never a disabled button, so an absent capability makes its control inert and
 * says so out loud instead of rendering a button that would do nothing.
 *
 * Every opener returns a boolean rather than `void` because F-JR-20 gates every
 * writing route on a verified-ready library, and `begin…` returning false is
 * how a refusal reaches the surface that has to print the reason.
 */

import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import type {
  JournalDraft,
  JournalEntryProjection,
  JournalGoal,
  JournalPromptReference,
  JournalRecoveryItem,
} from "@ff/domain";

/** What F-JR-02, F-JR-06 and F-JR-40 hand the one shared composer (F-JR-19). */
export interface JournalWritingSeed {
  /** Absent for a blank entry; the prompt's family also decides the kind. */
  readonly prompt?: JournalPromptReference;
  readonly goal?: JournalGoal;
}

export interface JournalHomeHost {
  /**
   * F-JR-02 / F-JR-06: opens the composer on a blank or prompted entry.
   * `false` means the composer gate refused — the library was not ready — and
   * the caller prints F-JR-20's refusal rather than assuming it opened.
   */
  readonly beginWriting?: (seed: JournalWritingSeed) => boolean;
  /** F-JR-05 / F-JR-14: reopens an autosaved draft where it was left. */
  readonly resumeDraft?: (draft: JournalDraft) => boolean;
  /**
   * F-JR-13: opens the reader on a projection.
   *
   * A projection rather than an id, because F-JR-43 says the reader shows the
   * draft-overlaid working truth, and handing over an id would let the reader
   * re-read the older durable revision.
   */
  readonly openReader?: (projection: JournalEntryProjection) => void;
  /** F-JR-08: the Goals workspace card, shown only when the lens is Goals. */
  readonly openGoals?: () => void;
  /**
   * F-JR-04: re-verifies the preserved bytes and stages a copy. Absent means
   * this build cannot export, and the row's button becomes inert.
   */
  readonly exportRecovery?: (item: JournalRecoveryItem) => Promise<boolean>;
}

const EMPTY_HOST: JournalHomeHost = Object.freeze({});

const JournalHomeHostContext = createContext<JournalHomeHost>(EMPTY_HOST);

export interface JournalHomeHostProviderProps {
  readonly host: JournalHomeHost;
  readonly children?: ReactNode;
}

export function JournalHomeHostProvider({
  host,
  children,
}: JournalHomeHostProviderProps): ReactNode {
  return (
    <JournalHomeHostContext.Provider value={host}>{children}</JournalHomeHostContext.Provider>
  );
}

/**
 * Defaults to the empty host rather than throwing, unlike `usePorts()`. A
 * missing port is a composition bug; a missing *host capability* is the
 * ordinary state of this build, and Journal has to render honestly without it.
 */
export function useJournalHomeHost(): JournalHomeHost {
  return useContext(JournalHomeHostContext);
}
